/**
 * @fileoverview Page-level filters of the Sales Performance screen.
 * @description The Period dropdown, the Team dropdown (only for a caller whose scope allows a
 * team filter) and the Export CSV button (only for a caller who may export), shown in the page
 * header. The options come from the summary, so they always match what the backend will accept;
 * the component holds no state of its own.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { ALL_TEAMS } from '@/features/sales/lib/sales-performance.utils'
import Button from '@/shared/components/atoms/Button'
import Select from '@/shared/components/atoms/Select'

import type {
  SalesPerformancePeriodCode,
  SalesPerformanceSummary,
} from '@/features/sales/types/sales-performance.types'
import type { ChangeEvent, ReactNode } from 'react'

interface SalesPerformanceFiltersProps {
  summary: SalesPerformanceSummary | undefined
  period: SalesPerformancePeriodCode
  team: string
  isExporting: boolean
  onPeriodChange: (period: SalesPerformancePeriodCode) => void
  onTeamChange: (team: string) => void
  onExport: () => void
}

/**
 * @description Turns a period option into a dropdown option.
 * @param {{ code: string; label: string }} option - The summary's period option.
 * @returns {{ value: string; label: string }} The dropdown option.
 */
function toPeriodOption(option: { code: string; label: string }) {
  return { value: option.code, label: option.label }
}

/**
 * @description Turns a team option into a dropdown option.
 * @param {{ key: string; label: string }} option - The summary's team option.
 * @returns {{ value: string; label: string }} The dropdown option.
 */
function toTeamOption(option: { key: string; label: string }) {
  return { value: option.key, label: option.label }
}

/**
 * @component
 * @param {{ label: string; children: ReactNode }} props - The control and the label above it.
 */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-slate">{label}</span>
      {children}
    </div>
  )
}

/**
 * @component
 * @param {SalesPerformanceFiltersProps} props - The summary the options come from, the selection
 * and the handlers.
 * @param {SalesPerformanceSummary | undefined} props.summary - The latest summary, if loaded.
 * @param {SalesPerformancePeriodCode} props.period - The selected period.
 * @param {string} props.team - The selected team key, or `ALL`.
 * @param {boolean} props.isExporting - Whether an export is in flight.
 * @param {(period: SalesPerformancePeriodCode) => void} props.onPeriodChange - Called with the
 * chosen period.
 * @param {(team: string) => void} props.onTeamChange - Called with the chosen team key.
 * @param {() => void} props.onExport - Called when Export CSV is clicked.
 */
function SalesPerformanceFilters({
  summary,
  period,
  team,
  isExporting,
  onPeriodChange,
  onTeamChange,
  onExport,
}: SalesPerformanceFiltersProps) {
  const { t } = useTranslation()
  const periodOptions = summary?.periodOptions ?? [{ code: period, label: period }]

  /**
   * @description Reports the chosen period.
   * @param {ChangeEvent<HTMLSelectElement>} event - The change event of the Period dropdown.
   */
  const handlePeriodChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onPeriodChange(event.target.value as SalesPerformancePeriodCode)
  }

  /**
   * @description Reports the chosen team.
   * @param {ChangeEvent<HTMLSelectElement>} event - The change event of the Team dropdown.
   */
  const handleTeamChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onTeamChange(event.target.value)
  }

  return (
    <>
      <Field label={t('sales.salesPerformance.filters.period')}>
        <Select
          value={period}
          aria-label={t('sales.salesPerformance.filters.period')}
          data-testid="sales-performance-period-select"
          onChange={handlePeriodChange}
          options={periodOptions.map(toPeriodOption)}
        />
      </Field>
      {summary?.scope.canFilterTeam && (
        <Field label={t('sales.salesPerformance.filters.team')}>
          <Select
            value={team}
            aria-label={t('sales.salesPerformance.filters.team')}
            data-testid="sales-performance-team-select"
            onChange={handleTeamChange}
            options={[
              { value: ALL_TEAMS, label: t('sales.salesPerformance.filters.allTeams') },
              ...summary.teamOptions.map(toTeamOption),
            ]}
          />
        </Field>
      )}
      {summary?.scope.canExport && (
        <div className="w-fit">
          <Button
            type="button"
            size="md"
            variant="secondary"
            onClick={onExport}
            isLoading={isExporting}
            data-testid="sales-performance-export-button"
          >
            {t('sales.salesPerformance.export')}
          </Button>
        </div>
      )}
    </>
  )
}

export default SalesPerformanceFilters
